/**
 * Eigene Bilder und Videos eines Arbeitsschritts.
 *
 * Bilder erscheinen als Vorschau und öffnen sich beim Antippen groß. Videos
 * spielen direkt an Ort und Stelle; geladen wird erst beim Abspielen. Im
 * Druck steht statt eines Videos sein Vorschaubild.
 */
import { useEffect, useRef, useState } from 'react';
import type { MediaItem } from '../api/types.ts';
import { texts } from '../texts/de.ts';

/** Eigenschaften der Schrittmedien. */
export interface StepMediaProps {
  /** Fehlt bei Rezepten, die vor dieser Funktion im Gerät gespeichert wurden. */
  readonly media: readonly MediaItem[] | undefined;
  /** Größere Darstellung im Kochmodus. */
  readonly large?: boolean;
  readonly className?: string;
}

/** Großansicht eines Bildes. */
function ImageViewer({ item, onClose }: { readonly item: MediaItem; readonly onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => element?.close();
  }, []);

  return (
    <dialog
      ref={dialog}
      onClose={onClose}
      onClick={(event) => {
        // Klick neben das Bild schließt.
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
      className="m-auto max-h-[92vh] max-w-[94vw] rounded-card bg-surface-raised p-2 backdrop:bg-black/70"
    >
      <img
        src={item.url}
        alt={item.originalName ?? ''}
        className="max-h-[80vh] max-w-full rounded-lg object-contain"
      />
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm"
        >
          {texts.common.close}
        </button>
      </div>
    </dialog>
  );
}

/** Bilder und Videos eines Schritts. */
export function StepMedia({ media, large = false, className = '' }: StepMediaProps) {
  const [open, setOpen] = useState<MediaItem | null>(null);
  if (media === undefined || media.length === 0) {
    return null;
  }
  const size = large ? 'h-48 sm:h-56' : 'h-32 print:h-24';

  return (
    <>
      <ul
        className={`flex snap-x gap-2 overflow-x-auto pb-1 print:flex-wrap print:overflow-visible ${className}`}
        aria-label={texts.editor.stepMedia}
      >
        {media.map((item) => (
          <li key={item.id} className="shrink-0 snap-start print:break-inside-avoid">
            {item.kind === 'video' ? (
              <>
                <video
                  src={item.url}
                  poster={item.thumbUrl}
                  controls
                  playsInline
                  preload="none"
                  className={`${size} max-w-[85vw] rounded-lg bg-surface-sunken print:hidden`}
                />
                <img
                  src={item.thumbUrl}
                  alt={item.originalName ?? ''}
                  className={`hidden ${size} rounded-lg object-cover print:block`}
                />
              </>
            ) : (
              <button
                type="button"
                onClick={() => setOpen(item)}
                aria-label={item.originalName ?? texts.editor.stepMedia}
                className="block overflow-hidden rounded-lg"
              >
                <img
                  src={item.thumbUrl}
                  alt={item.originalName ?? ''}
                  loading="lazy"
                  className={`${size} w-auto max-w-[85vw] object-cover`}
                />
              </button>
            )}
          </li>
        ))}
      </ul>
      {open === null ? null : <ImageViewer item={open} onClose={() => setOpen(null)} />}
    </>
  );
}
